import React, { useState, useEffect, useMemo } from 'react';
import { useDeleteConfirm } from '../hooks/useDeleteConfirm';
import { friendlyDeleteError } from '../utils/deleteError';
import idColumn, { RecordLabelText } from './ui/idColumn';
import { ParsedVendor, PRICE_RANGE_OPTIONS } from '../data/vendorData';
import { EVENTS_CHANGED_EVENT, EVENTS_LOADED_EVENT, eventsLoaded, loadEvents, refreshEventsFromApi, StoredEvent } from '../services/EventsService';
import { loadBookings, VENDOR_BOOKINGS_UPDATED_EVENT } from '../services/VendorBookingService';
import { VendorBooking } from '../types/Vendor';
import { apiPut, apiDelete, planningPath } from '../services/PlanningApiClient';
import Icon from './Icon';
import EmptyState from './EmptyState';
import DataTable from './DataTable';
import ContentCard from './ContentCard';
import VendorBrief from './VendorBrief';
import VendorRecommendations from './VendorRecommendations';
import VendorLinks from './VendorLinks';
import { MetricCard } from './MetricCards';
import ConfirmDialog from './ui/ConfirmDialog';
import StatusBadge from './ui/StatusBadge';
import TagMultiSelect from './ui/TagMultiSelect';
import RichTextEditor from './ui/RichTextEditor';
import SearchableSelect from './ui/SearchableSelect';
import '../styles/components/VendorTable.css';
import { fmtId } from '../utils/jiraId';
import { VENDOR_BOOKING_STATUS_LABELS, VENDOR_BOOKING_STATUS_COLORS } from '../utils/vendorBookingStatus';
import { PHONE_PLACEHOLDER } from '../constants/placeholders';

interface VendorTableProps {
  vendors: ParsedVendor[];
  onVendorClick: (vendor: ParsedVendor) => void;
  onRateVendor?: (vendor: ParsedVendor) => void;
  onSelectionChange?: (selectedVendors: ParsedVendor[]) => void;
  vendorId?: string;
  viewMode?: 'table' | 'cards';
  onViewModeChange?: (view: 'table' | 'cards') => void;
  showArchived?: boolean;
}

const VendorTable: React.FC<VendorTableProps> = ({ vendors, onVendorClick, onRateVendor, onSelectionChange, vendorId, viewMode: externalViewMode, onViewModeChange, showArchived }) => {
  const [internalViewMode, setInternalViewMode] = useState<'table' | 'cards'>('table');
  const viewMode = externalViewMode ?? internalViewMode;
  const setViewMode = (v: 'table' | 'cards') => { setInternalViewMode(v); onViewModeChange?.(v); };
  const [bookings, setBookings] = useState<Record<string, VendorBooking>>({});
  const [archiveTarget, setArchiveTarget] = useState<string | null>(null);
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [archiving, setArchiving] = useState(false);
  // Delete forever is only offered from the archived list.
  const forever = useDeleteConfirm<{ id: string; name: string }>({
    record: 'vendor',
    perform: v => apiDelete(planningPath(`/vendors/${v.id}`)),
    onDone: () => { window.location.reload(); },
  });
  const [editForm, setEditForm] = useState<{
    name: string; description: string; priceRange: string;
    phone: string; email: string; website: string; address: string;
    instagram: string; services: string[]; teamTag: string[];
  } | null>(null);

  // Resolve the slug-based API key for the currently-viewed vendor.
  // After the ID migration, vendor.id is the numeric PK; vendor.apiKey is the slug for API paths.
  const vendorApiKey = useMemo(() => {
    if (!vendorId) return undefined;
    const v = vendors.find(vv => vv.id === vendorId);
    return v?.apiKey ?? vendorId;
  }, [vendorId, vendors]);

  // Initialize edit form when viewing a vendor
  useEffect(() => {
    if (!vendorId) { setEditForm(null); return; }
    const v = vendors.find(vv => vv.id === vendorId);
    if (v) {
      setEditForm({
        name: v.name,
        description: v.description || '',
        priceRange: v.priceRange || '',
        phone: v.contact?.phone || '',
        email: v.contact?.email || '',
        website: v.contact?.website || '',
        address: v.contact?.address || '',
        instagram: v.instagramHandle || '',
        services: v.services,
        teamTag: v.teamTag ? v.teamTag.split(',').map(t => t.trim()).filter(Boolean) : [],
      });
    }
  }, [vendorId, vendors]);

  // Listen for header save/delete events
  useEffect(() => {
    if (!vendorId || !editForm) return;
    const onSave = () => {
      apiPut(planningPath(`/vendors/${vendorApiKey ?? vendorId}`), {
        name: editForm.name,
        description: editForm.description,
        price_range: editForm.priceRange,
        phone: editForm.phone,
        email: editForm.email,
        website: editForm.website,
        address: editForm.address,
        instagram: editForm.instagram,
        services: editForm.services,
        ...(editForm.teamTag.length > 0 ? { team_tag: editForm.teamTag.join(',') } : {}),
      }).catch(() => {}).finally(() => { window.location.href = '/vendors'; });
    };
    const onDel = () => {
      setArchiveError(null);
      setArchiveTarget(vendorApiKey ?? vendorId);
    };
    window.addEventListener('vendor-save', onSave);
    window.addEventListener('vendor-delete', onDel);
    return () => { window.removeEventListener('vendor-save', onSave); window.removeEventListener('vendor-delete', onDel); };
  }, [vendorId, vendorApiKey, editForm]);
  const [events, setEvents] = useState<StoredEvent[]>([]);

  useEffect(() => {
    const refreshBookings = () => setBookings(loadBookings());

    refreshBookings();
    window.addEventListener(VENDOR_BOOKINGS_UPDATED_EVENT, refreshBookings as EventListener);
    window.addEventListener('storage', refreshBookings);

    return () => {
      window.removeEventListener(VENDOR_BOOKINGS_UPDATED_EVENT, refreshBookings as EventListener);
      window.removeEventListener('storage', refreshBookings);
    };
  }, []);

  useEffect(() => {
    const refreshEvents = () => setEvents(loadEvents());

    refreshEvents();
    if (!eventsLoaded()) void refreshEventsFromApi();
    window.addEventListener(EVENTS_CHANGED_EVENT, refreshEvents as EventListener);
    window.addEventListener(EVENTS_LOADED_EVENT, refreshEvents as EventListener);
    window.addEventListener('storage', refreshEvents);

    return () => {
      window.removeEventListener(EVENTS_CHANGED_EVENT, refreshEvents as EventListener);
      window.removeEventListener(EVENTS_LOADED_EVENT, refreshEvents as EventListener);
      window.removeEventListener('storage', refreshEvents);
    };
  }, []);

  const getEventTitle = (eventId?: string) => events.find((event) => event.id === eventId)?.title;

  const handleRestoreVendor = (vendorIdToRestore: string) => {
    apiPut(planningPath(`/vendors/${vendorIdToRestore}`), { archived: false })
      .then(() => { window.location.reload(); })
      // Previously .catch(() => {}) then reloaded regardless, so a failed
      // restore was indistinguishable from a successful one.
      .catch(err => window.alert(err instanceof Error ? err.message : 'Failed to restore vendor.'));
  };

  const handlePermanentDeleteVendor = (vendorIdToDelete: string, name: string) => {
    forever.request({ id: vendorIdToDelete, name });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word[0])
      .join('')
      .slice(0, 2)
      .toUpperCase();
  };

  // DataTable sorts on the raw field value, and a naive ascending sort on
  // shortlistRank puts every unranked (null) vendor above #1. Decorate each
  // row with a numeric sort key that pushes "no assessment" to the end.
  const tableRows = useMemo(
    () => vendors.map(v => ({
      ...v,
      shortlistSort: typeof v.shortlistRank === 'number' ? v.shortlistRank : Number.MAX_SAFE_INTEGER,
    })),
    [vendors]
  );

  const columns = [
    idColumn<ParsedVendor>('vendor'),
    {
      key: 'name',
      header: 'Name',
      sortable: true,
      width: '280px',
      render: (value: string, row: ParsedVendor) => (
        <div className="vendor-info dt-link-cell" onClick={() => onVendorClick(row)}>
          <div className="vendor-avatar-circle">
            {getInitials(row.name)}
          </div>
          <div className="vendor-text">
            <span className="vendor-primary-name">{row.name}</span>
            <span className="vendor-subtext">
              {/* The handle as stored sometimes already carries its @, so
                  prepending one unconditionally produced "@@2706events". */}
              {row.instagramHandle
                ? `@${String(row.instagramHandle).replace(/^@+/, '')}`
                : <RecordLabelText kind="vendor" id={row.id} />}
            </span>
            {/* Rating sits under the name rather than in a column of its own.
                It had been a passive star column immediately left of the star
                BUTTON that opens the rating page — two adjacent star columns,
                one of which was not about rating at all in archived mode. */}
            {typeof row.rating === 'number' && row.rating > 0 && (
              <span className="vendor-rating-inline" title={`Average rating ${row.rating.toFixed(1)}`}>
                <Icon name="star" size={11} /> {row.rating.toFixed(1)}
              </span>
            )}
            {(row.pressFeatureCount ?? 0) > 0 && (
              <span className="vendor-press-badge" title="Number of BellaNaija wedding articles this vendor appeared in">
                ✦ {row.pressFeatureCount}× BellaNaija
              </span>
            )}
          </div>
        </div>
      )
    },
    {
      key: 'services',
      header: 'Services',
      sortable: false,
      width: '150px',
      render: (value: unknown) => {
        // Insert space before each capital letter — fixes concatenated scraped service strings
        // e.g. "ConceptdesignPlanningandStyling" → "Conceptdesign Planning and Styling"
        // Also guards against service objects {name, price} from stale localStorage cache
        const fmt = (s: unknown) => {
          const str = typeof s === 'string' ? s : (s as any)?.name ?? String(s ?? '');
          return str.replace(/([A-Z])/g, ' $1').trim();
        };
        const arr = Array.isArray(value) ? value : [];
        return (
          <div className="tag-pills">
            {arr.slice(0, 2).map((service, idx) => (
              <span key={idx} className="ds-badge ds-badge-neutral">{fmt(service)}</span>
            ))}
            {arr.length > 2 && (
              <span className="ds-badge ds-badge-neutral">+{arr.length - 2}</span>
            )}
          </div>
        );
      }
    },
    {
      key: 'shortlistSort',
      header: 'Shortlist',
      sortable: true,
      width: '110px',
      render: (_value: number, row: ParsedVendor) => {
        if (typeof row.shortlistRank !== 'number') {
          return <span className="vt-shortlist-empty">—</span>;
        }
        return (
          <div className="vt-shortlist-cell">
            <span className="ds-badge ds-badge-success">#{row.shortlistRank}</span>
            {row.shortlistTier && <span className="vt-shortlist-tier">{row.shortlistTier}</span>}
          </div>
        );
      }
    },
    {
      key: 'priceRange',
      header: 'Price Range',
      sortable: true,
      width: '90px',
      render: (value: string) => (
        <span className="ds-badge ds-badge-neutral">{value}</span>
      )
    },
    {
      key: 'availability',
      header: 'Availability',
      sortable: false,
      width: '150px',
      render: (value: unknown[] | undefined) => {
        const items = Array.isArray(value) ? value : [];
        if (items.length === 0) {
          return <span className="form-hint" style={ { color: '#9ca3af' }}>—</span>;
        }
        return (
          <div className="tag-pills">
            {items.slice(0, 2).map((slot: unknown, idx) => (
              <span key={idx} className="ds-badge ds-badge-neutral">{typeof slot === 'string' ? slot : (slot as any)?.name ?? String(slot ?? '')}</span>
            ))}
            {items.length > 2 && (
              <span className="ds-badge ds-badge-neutral">+{items.length - 2}</span>
            )}
          </div>
        );
      }
    },
    {
      key: 'sourceEvents',
      header: 'Source',
      sortable: false,
      width: '100px',
      render: (value: ParsedVendor['sourceEvents'] | undefined, row: ParsedVendor) => {
        const count = Array.isArray(value) ? value.length : 0;
        if (count > 0) {
          const sourceName = value?.[0]?.url?.includes('bellanaija') ? 'BellaNaija' : 'Scraped';
          return (
            <span className="ds-badge ds-badge-neutral">
              {sourceName}{count > 1 ? ` ×${count}` : ''}
            </span>
          );
        }
        return (
          <span className="ds-badge ds-badge-neutral">Manual</span>
        );
      }
    },
    {
      key: 'verified',
      header: 'Latest Status',
      sortable: false,
      width: '110px',
      render: (value: boolean, row: ParsedVendor) => {
        // Slug-keyed, like every other read of this cache — row.id is the
        // numeric directory id, which it is never keyed by.
        const b = row.apiKey ? bookings[row.apiKey] : undefined;
        if (b) {
          const statusColors = VENDOR_BOOKING_STATUS_COLORS;
          const statusLabels = VENDOR_BOOKING_STATUS_LABELS;
          return (
            <div style={ { display: 'flex', flexDirection: 'column', gap: '4px', alignItems: 'flex-start' }}>
              <span
                className="ds-badge ds-badge-dynamic"
                style={ { '--ds-badge-accent': statusColors[b.status] ?? statusColors.inquiry } as React.CSSProperties}
              >
                {/* Every other status render in the app has a fallback; this
                    pair was the only unguarded one, so an unmapped status drew
                    a blank chip. The service now validates too — this is the
                    belt to that braces. */}
                {statusLabels[b.status] ?? 'Inquiry'}
              </span>
              {b.eventId && getEventTitle(b.eventId) ? (
                <span style={ { fontSize: '0.75rem', color: '#6b7280' }}>
                  {getEventTitle(b.eventId)}
                </span>
              ) : null}
            </div>
          );
        }
        return (
          <div className="status-indicator">
            <span className={`ds-badge ${value ? 'ds-badge-success' : 'ds-badge-warning'}`}>
              {value ? 'Verified' : 'Pending'}
            </span>
          </div>
        );
      }
    },
    // Archived-mode Restore/Delete stay — they are NOT reachable by clicking
    // the row (the row opens the vendor, which no longer exists to open once
    // archived semantics diverge from active). The active-mode "Rate" star
    // is gone: the row already navigates to the vendor, and Rate is a header
    // action on that same detail page (onRateVendorAction in Header.tsx) —
    // a second path to the same place, occupying a whole column for it.
    ...(showArchived ? [{
      key: 'actions',
      header: '',
      sortable: false,
      render: (value: any, row: ParsedVendor) => (
        <div style={ { display: 'flex', gap: '8px' }}>
          <button
            style={ { cursor: 'pointer', background: '#16a34a18', color: '#16a34a', border: '1px solid #16a34a44', borderRadius: '6px', padding: '4px 10px', fontSize: '0.75rem', fontWeight: 600 }}
            onClick={(e) => { e.stopPropagation(); handleRestoreVendor(row.apiKey ?? row.id); }}
            title="Restore vendor"
          >
            Restore
          </button>
          <button
            style={ { cursor: 'pointer', background: '#dc262618', color: '#dc2626', border: '1px solid #dc262644', borderRadius: '6px', padding: '4px 10px', fontSize: '0.75rem', fontWeight: 600 }}
            onClick={(e) => { e.stopPropagation(); handlePermanentDeleteVendor(row.apiKey ?? row.id, row.name); }}
            title="Delete vendor forever"
          >
            Delete
          </button>
        </div>
      )
    }] : [])
  ];

  // Find selected vendor if vendorId is provided
  const selectedVendor = vendorId ? vendors.find(v => v.id === vendorId) : null;

  return (
    <div className="wt-container fade-in">
      {/* Show detail view if vendorId is provided */}
      {selectedVendor && editForm ? (
        <div className="detail-layout">
          <div className="inline-form-page">
            {/* ── Basic info ─────────────────────────────── */}
            <div className="form-row">
              <div className="form-group">
                <label>Vendor Name</label>
                <input type="text" value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} />
              </div>
              <div className="form-group">
                <label>Price Range</label>
                <SearchableSelect
                  value={editForm.priceRange}
                  onChange={value => setEditForm({ ...editForm, priceRange: value })}
                  placeholder="Select tier"
                  options={[...PRICE_RANGE_OPTIONS]}
                />
              </div>
            </div>

            <RichTextEditor
              label="Description"
              value={editForm.description}
              onChange={v => setEditForm({ ...editForm, description: v })}
              rows={3}
            />


            {/* ── Contact details ────────────────────────── */}
            <div className="form-row vt-contact-details-row">
              <div className="form-group">
                <label>Phone</label>
                <input type="tel" value={editForm.phone} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} placeholder={PHONE_PLACEHOLDER} />
              </div>
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={editForm.email} onChange={e => setEditForm({ ...editForm, email: e.target.value })} placeholder="vendor@example.com" />
              </div>
            </div>

            {/* ── Links & social ─────────────────────────── */}
            <div className="form-row">
              <div className="form-group">
                <label>Website</label>
                <input type="url" value={editForm.website} onChange={e => setEditForm({ ...editForm, website: e.target.value })} placeholder="https://…" />
              </div>
              <div className="form-group">
                <label>Location</label>
                <input type="text" value={editForm.address} onChange={e => setEditForm({ ...editForm, address: e.target.value })} placeholder="City, Country" />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Instagram Handle</label>
                <input type="text" value={editForm.instagram} onChange={e => setEditForm({ ...editForm, instagram: e.target.value })} placeholder="@handle" />
              </div>
              <div className="form-group" />
            </div>

            {/* ── Classification ─────────────────────────── */}
            <div className="form-group">
              <label>Services</label>
              <TagMultiSelect
                values={editForm.services}
                onChange={services => setEditForm({ ...editForm, services })}
                options={['Photography', 'Videography', 'Planning', 'Catering', 'Decoration', 'Music', 'Transportation', 'Accommodation', 'Other']}
                placeholder="Select or add services…"
                allowCreate
              />
            </div>
            <div className="form-group">
              <label>Team Tag</label>
              <TagMultiSelect
                values={editForm.teamTag}
                onChange={teamTag => setEditForm({ ...editForm, teamTag })}
                options={['photographer', 'DJ', 'content-creator', 'videographer', 'planner', 'decorator', 'caterer', 'coordinator']}
                placeholder="Select or add team tags…"
                allowCreate
              />
              <span className="form-hint">Match this to vendor/team tag on guidelines and tasks.</span>
            </div>
          </div>

          {/* Vendor Brief sidebar */}
          <aside className="detail-sidebar">
            <div className="side-section">
              {selectedVendor.apiKey && bookings[selectedVendor.apiKey]?.contractLink && (
                <div className="stat-row">
                  <span>Contract</span>
                  <a
                    href={bookings[selectedVendor.apiKey!].contractLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="vt-contract-link"
                  >
                    Open ↗
                  </a>
                </div>
              )}
              <div className="stat-row">
                <span>Rating</span>
                <strong>
                  {selectedVendor.rating && typeof selectedVendor.rating === 'number'
                    ? `${selectedVendor.rating.toFixed(1)}${selectedVendor.reviewCount ? ` (${selectedVendor.reviewCount})` : ''}`
                    : 'No rating'}
                </strong>
              </div>
              <div className="stat-row">
                <span>Status</span>
                <StatusBadge variant={selectedVendor.verified ? 'success' : 'neutral'}>
                  {selectedVendor.verified ? 'Verified' : 'Pending'}
                </StatusBadge>
              </div>
            </div>
            {typeof selectedVendor.shortlistRank === 'number' && (
              <section className="side-section">
                <div className="side-section-header">
                  <h4>
                    <Icon name="trophy" size={14} />
                    Shortlist assessment
                  </h4>
                </div>
                <div className="stat-row">
                  <span>Rank</span>
                  <strong>#{selectedVendor.shortlistRank}</strong>
                </div>
                {selectedVendor.shortlistTier && (
                  <div className="stat-row">
                    <span>Tier</span>
                    <strong>{selectedVendor.shortlistTier}</strong>
                  </div>
                )}
                {selectedVendor.bestFit && (
                  <div className="stat-row">
                    <span>Best fit</span>
                    <strong>{selectedVendor.bestFit}</strong>
                  </div>
                )}
              </section>
            )}
            <VendorBrief vendorId={selectedVendor.id} vendorTeamTag={selectedVendor.teamTag} credits={selectedVendor.credits} />
            <VendorLinks vendorId={selectedVendor.apiKey ?? selectedVendor.id} />
            <VendorRecommendations
              vendor={selectedVendor}
              onVendorSelect={(id) => { window.location.href = `/vendor/${fmtId('VD', id)}`; }}
            />
          </aside>
        </div>
      ) : (
        /* Show list view when no vendorId */
        <>
          {(() => {
            const booked = vendors.filter(v => {
              const b = v.apiKey ? bookings[v.apiKey] : undefined;
              return b && ['booked', 'contract-signed', 'paid-in-full'].includes(b.status);
            }).length;
            const verified = vendors.filter(v => v.verified).length;
            const rated = vendors.filter(v => typeof v.rating === 'number' && v.rating > 0);
            const avgRating = rated.length ? (rated.reduce((s, v) => s + v.rating, 0) / rated.length).toFixed(1) : 'N/A';
            return (
              <section className="stats-grid">
                <MetricCard label="Total Vendors" value={vendors.length} tone="pink" icon="briefcase" />
                <MetricCard label="Booked" value={booked} tone="blue" icon="check-circle" />
                <MetricCard label="Verified" value={verified} tone="teal" icon="check" />
                <MetricCard label="Avg Rating" value={avgRating} tone="orange" icon="star" />
              </section>
            );
          })()}
            {viewMode === 'table' ? (
              <DataTable
            pagination={{ enabled: true, pageSize: 25, showPageSizeSelector: true, pageSizeOptions: [25, 50, 100] }}
                recordList={{ kind: 'vendor' }}
                data={tableRows}
                columns={columns}
                onRowClick={onVendorClick}
                selectable={true}
                onSelectionChange={onSelectionChange}
                emptyMessage="No vendors found. Add some vendors to get started."
                hoverable={true}
              />
            ) : (
              <div className="vendor-cards-grid">
                {vendors.length === 0 ? (
                  <EmptyState icon="briefcase" title="No vendors found" description="Add some vendors to get started." />
                ) : (
                  vendors.map((vendor) => (
                    <ContentCard
                      key={vendor.id}
                      hideImage
                      title={vendor.name}
                      subtitle={(() => { const s0: unknown = vendor.services[0]; return (typeof s0 === 'string' ? s0 : (s0 as any)?.name ?? '') || 'Service Provider'; })()}
                      details={[
                        { label: 'Location', value: vendor.contact?.address || '—' },
                        ...(vendor.contact?.phone ? [{ label: 'Phone', value: vendor.contact.phone }] : []),
                        ...((vendor.pressFeatureCount ?? 0) > 0 ? [{ label: 'BellaNaija', value: `✦ ${vendor.pressFeatureCount}× featured` }] : []),
                      ]}
                      customActions={
                        <button
                          type="button"
                          className="vendor-card-rating"
                          title={onRateVendor ? 'Rate vendor' : 'Vendor rating'}
                          onClick={(e) => {
                            e.stopPropagation();
                            onRateVendor?.(vendor);
                          }}
                          style={ {
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            background: 'transparent',
                            border: 'none',
                            cursor: onRateVendor ? 'pointer' : 'default',
                            padding: 0,
                            color: 'inherit',
                            font: 'inherit',
                          }}
                        >
                          <Icon name="star" size={14} />
                          <span style={ { fontSize: '0.85rem', fontWeight: 500 }}>
                            {typeof vendor.rating === 'number' && vendor.rating > 0 ? vendor.rating.toFixed(1) : 'N/A'}
                          </span>
                        </button>
                      }
                      onClick={() => window.location.href = `/vendor/${fmtId('VD', vendor.id)}`}
                    />
                  ))
                )}
              </div>
            )}
        </>
      )}
      <ConfirmDialog
        open={archiveTarget !== null}
        onClose={() => { if (!archiving) setArchiveTarget(null); }}
        onConfirm={async () => {
          if (!archiveTarget || archiving) return;
          setArchiving(true);
          setArchiveError(null);
          try {
            await apiPut(planningPath(`/vendors/${archiveTarget}`), { archived: true });
            window.location.href = '/vendors';
          } catch (err) {
            setArchiveError(friendlyDeleteError(err, { record: 'vendor' }));
            setArchiving(false);
          }
        }}
        title="Archive this vendor?"
        description="You can restore it later from the Archived view."
        error={archiveError}
        loading={archiving}
        confirmLabel="Archive"
        variant="primary"
      />
      <ConfirmDialog
        {...forever.dialogProps}
        title="Delete this vendor forever?"
        subject={forever.target?.name}
        description="This cannot be undone."
        consequences={['The vendor and its contact details', 'Its notes and ratings']}
        confirmLabel="Delete forever"
      />
    </div>
  );
};

export default VendorTable;
